import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import marketplaceApiClient, {
  StateEntity,
  CityEntity,
  CountryEntity,
  InternationalCityEntity,
} from 'api/marketplaceApiClient';
import { RootState } from './rootReducer';

interface RegionState {
  states: StateEntity[];
  isStatesLoading: boolean;
  cities: CityEntity[];
  isCitiesLoading: boolean;
  countries: CountryEntity[];
  isCountriesLoading: boolean;
  countryCities: InternationalCityEntity[];
  isCountryCitiesLoading: boolean;
}

const initialState: RegionState = {
  states: [],
  isStatesLoading: false,
  cities: [],
  isCitiesLoading: false,
  countries: [],
  isCountriesLoading: false,
  countryCities: [],
  isCountryCitiesLoading: false,
};

const getAllCountries = createAsyncThunk(
  'region/getAllCountries',
  async () => {
    return marketplaceApiClient.getListCountries();
  },
  {
    condition: (_, { getState }) => {
      const { region } = getState() as RootState;

      return !region.countries.length;
    },
  },
);

const getAllCountryCities = createAsyncThunk(
  'region/getAllCountryCities',
  async () => {
    return marketplaceApiClient.getListInternationalCities();
  },
  {
    condition: (_, { getState }) => {
      const { region } = getState() as RootState;

      return !region.countryCities.length;
    },
  },
);

const getAllStates = createAsyncThunk(
  'region/getAllStates',
  async () => {
    return await marketplaceApiClient.getListStates();
  },
  {
    condition: (_, { getState }) => {
      const { region } = getState() as RootState;

      return !region.states.length;
    },
  },
);

const getAllCities = createAsyncThunk(
  'region/getAllCities',
  async () => {
    return await marketplaceApiClient.getListCities();
  },
  {
    condition: (_, { getState }) => {
      const { region } = getState() as RootState;

      return !region.cities.length;
    },
  },
);

const regionSlice = createSlice({
  name: 'region',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllStates.fulfilled, (state, action) => {
      state.states = action.payload;
      state.isStatesLoading = false;
    });
    builder.addCase(getAllStates.pending, (state, action) => {
      state.isStatesLoading = true;
    });
    builder.addCase(getAllStates.rejected, (state, action) => {
      state.isStatesLoading = false;
    });
    builder.addCase(getAllCities.fulfilled, (state, action) => {
      state.cities = action.payload;
      state.isCitiesLoading = false;
    });
    builder.addCase(getAllCities.rejected, (state, action) => {
      state.isCitiesLoading = false;
    });
    builder.addCase(getAllCities.pending, (state, action) => {
      state.isCitiesLoading = true;
    });
    builder.addCase(getAllCountries.fulfilled, (state, action) => {
      state.countries = action.payload;
      state.isCountriesLoading = false;
    });
    builder.addCase(getAllCountries.pending, (state, action) => {
      state.isCountriesLoading = true;
    });
    builder.addCase(getAllCountries.rejected, (state, action) => {
      state.isCountriesLoading = false;
    });
    builder.addCase(getAllCountryCities.fulfilled, (state, action) => {
      state.countryCities = action.payload;
      state.isCountryCitiesLoading = false
    });
    builder.addCase(getAllCountryCities.pending, (state, action) => {
      state.isCountryCitiesLoading = true
    });
    builder.addCase(getAllCountryCities.rejected, (state, action) => {
      state.isCountryCitiesLoading = false
    });
  },
});

const { reducer } = regionSlice;

export { getAllStates, getAllCities, getAllCountries, getAllCountryCities };
export default reducer;
